import { useMemo } from 'react'
import { AxiosError } from 'axios'
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { SortingState } from '@tanstack/table-core'
import { enqueueSnackbar } from '@applift/factor'
import { WithRecordResponse } from '../models/Response'
import { CmpConversion, CmpConversionSelectItem } from '../models/Conversion'
import {
  getConversionsByIdsForCmp,
  getSelectConversionsListForCmp,
} from '../api/list'
import {
  getConversionsByIdsForCmpKey,
  getSelectConversionsListForCmpKey,
} from '../api/queryKey'
import { queryClient } from '../cache'
import { sortingString } from '../utils/sorting'

type List = WithRecordResponse<CmpConversion[]>

export const useSelectedConversionsDisplayTableData = (
  conversionIds: number[],
  options?: {
    onSuccess?: UseQueryOptions<List>['onSuccess']
    onError?: UseQueryOptions<List>['onError']
    enabled?: UseQueryOptions<List>['enabled']
  }
): UseQueryResult<List> => {
  const ids = useMemo(() => conversionIds.sort().join(','), [conversionIds])

  const queryData = useQuery(
    getConversionsByIdsForCmpKey.keys({
      scope: 'getConversionsByIdsForCmp',
      conversionIds: ids,
    }),
    getConversionsByIdsForCmp,
    {
      onSuccess: options?.onSuccess,
      onError: (e: AxiosError['response']) => {
        if (enqueueSnackbar) {
          enqueueSnackbar(
            e?.data?.errorObjects
              ? (e.data?.errorObjects[0]?.error as string)
              : 'Something went wrong!',
            {
              variant: 'error',
            }
          )
        }
        if (options?.onError) {
          options.onError(e as any)
        }
      },
      enabled: options?.enabled ?? true,
      cacheTime: Infinity,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    }
  )

  return queryData
}

export const refetchSelectedConversionsTable = () => {
  queryClient.removeQueries({
    predicate: (query: any) =>
      query.queryKey?.[0]?.scope === 'getConversionsByIdsForCmp',
  })
}

type SelectList = WithRecordResponse<CmpConversionSelectItem[]>

export const useSelectConversionsTableData = (
  _excludeConversionIds: number[],
  owId: number,
  conversionTypeId: number,
  _sortBy: SortingState,
  searchField?: string,
  limit?: number,
  options?: {
    onSuccess?: UseInfiniteQueryOptions<SelectList>['onSuccess']
    onError?: UseInfiniteQueryOptions<SelectList>['onError']
  }
): UseInfiniteQueryResult<SelectList> => {
  const excludeConversionIds = useMemo(
    () => _excludeConversionIds.sort().join(','),
    [_excludeConversionIds]
  )
  const sortBy = useMemo(() => sortingString(_sortBy).sortBy || '', [_sortBy])

  const queryData = useInfiniteQuery(
    getSelectConversionsListForCmpKey.keys({
      scope: 'getSelectConversionsListForCmp',
      excludeConversionIds,
      owId,
      conversionTypeId,
      searchField,
      sortBy,
      limit,
    }),
    getSelectConversionsListForCmp,
    {
      getNextPageParam: (lastPage, pages) => {
        const totalRecordsFetched = pages.reduce(
          (prev, one) => prev + one.data?.data?.length,
          0
        )
        if (totalRecordsFetched < lastPage?.data?.filteredRecords) {
          return pages.length + 1
        }
        return
      },
      onSuccess: options?.onSuccess,
      onError: (e: AxiosError['response']) => {
        if (enqueueSnackbar) {
          enqueueSnackbar(
            e?.data?.errorObjects
              ? (e.data?.errorObjects[0]?.error as string)
              : 'Something went wrong!',
            {
              variant: 'error',
            }
          )
        }
        if (options?.onError) {
          options.onError(e as any)
        }
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      cacheTime: 0,
    }
  )

  return queryData
}

export const refetchSelectConversionsTable = () => {
  queryClient.removeQueries({
    predicate: (query: any) =>
      query.queryKey?.[0]?.scope === 'getSelectConversionsListForCmp',
  })
}
